import {
  Calendar, Clock, MapPin, Phone, User, CreditCard,
  FileText, Home as HomeIcon, Euro, Copy, CheckCircle2, Zap,
  Camera, Receipt, Download, AlertTriangle, Plus, UserCheck,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import CleaningScope from "../../components/CleaningScope";
import { statusLabel } from "./ClientDashboard";

/**
 * Shared booking detail body — used inside the modal on `/cliente/reservas`
 * and on the standalone page `/cliente/reservas/:bid`. All dynamic content
 * comes from a single `booking` object as returned by `GET /bookings/:id`.
 */
export default function BookingDetailContent({ booking, onCancel, onEdit }) {
  if (!booking) return null;
  const b = booking;
  const paid = b.payment_status === "paid";
  const cancellable = ["pending", "confirmed"].includes(b.status);
  const address = [b.address_street, b.address_number].filter(Boolean).join(", ");
  const addressLine2 = [b.address_postal_code, b.address_council].filter(Boolean).join(" · ");

  function copy(txt, label = "valor") {
    navigator.clipboard?.writeText(txt).then(
      () => toast.success(`${label} copiado`),
      () => toast.error("Não foi possível copiar"),
    );
  }

  return (
    <div className="space-y-6" data-testid="booking-detail-content">
      {/* Header chips */}
      <div className="flex items-center gap-3 flex-wrap">
        <StatusBadge status={b.status} />
        {b.payment_reference && (
          <button onClick={() => copy(b.payment_reference, "Código da reserva")}
                  className="text-[10px] uppercase tracking-widest text-[#a0b2af] hover:text-[#d4af37] font-mono inline-flex items-center gap-1.5"
                  data-testid="booking-ref-copy">
            {b.payment_reference} <Copy size={10} />
          </button>
        )}
        {b.created_at && (
          <span className="text-[10px] uppercase tracking-widest text-[#a0b2af]">
            Criada em {formatDateTime(b.created_at)}
          </span>
        )}
      </div>

      {/* Payment status — Stripe removed; payment is now manual MBWay /
          transferência confirmed by the admin who emits the Fatura-Recibo
          via Facturalusa. The booking detail elsewhere shows the IBAN/MBWay
          and the upload-proof component if applicable. */}
      {!paid && (
        <div className="lux-card p-4 flex items-center gap-3 border-[#d4af37] bg-[#d4af37]/5"
             data-testid="detail-payment-status">
          <div className="w-9 h-9 rounded-sm bg-[#d4af37]/20 border border-[#d4af37]/50 flex items-center justify-center text-[#d4af37]">
            <Zap size={16} />
          </div>
          <div className="flex-1">
            <div className="eyebrow text-[#d4af37]">Pagamento pendente</div>
            <div className="text-sm mt-0.5">
              O <strong className="text-[#d4af37]">valor final</strong> é confirmado por contacto
              telefónico ou e-mail. Entre em contacto pelo <strong>WhatsApp +351 967 386 076</strong>{" "}
              ou <strong>luxstaymadeira@gmail.com</strong>. A fatura é emitida após o pagamento.
            </div>
          </div>
        </div>
      )}

      {/* Completed report CTA */}
      {b.status === "completed" && (
        <Link to={`/cliente/reservas/${b.id}/relatorio`}
              className="lux-card p-4 flex items-center gap-3 border-[#10b981]/60 bg-[#10b981]/5 hover:bg-[#10b981]/10 transition-colors"
              data-testid="detail-report-cta">
          <div className="w-9 h-9 rounded-sm bg-[#10b981]/20 border border-[#10b981]/60 flex items-center justify-center text-[#10b981]">
            <Camera size={16} />
          </div>
          <div className="flex-1">
            <div className="eyebrow text-[#10b981]">Limpeza concluída</div>
            <div className="text-sm mt-0.5 text-[#fdfbf7]">
              {b.report_summary ? (
                <>
                  {b.report_summary.photos_count} fotografias
                  {b.report_summary.issues_count > 0 && ` · ${b.report_summary.issues_count} problema${b.report_summary.issues_count === 1 ? "" : "s"}`}
                  {b.report_summary.extras_count > 0 && ` · ${b.report_summary.extras_count} extra${b.report_summary.extras_count === 1 ? "" : "s"}`}
                </>
              ) : "Ver fotografias, problemas e extras"}
            </div>
          </div>
          <span className="text-xs uppercase tracking-widest text-[#10b981]">Relatório →</span>
        </Link>
      )}

      {/* Photo preview strip */}
      {b.status === "completed" && b.report_summary?.photos_preview?.length > 0 && (
        <section className="lux-card p-5" data-testid="detail-photos-preview">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="eyebrow flex items-center gap-2 text-[#d4af37]">
              <Camera size={12} /> Fotografias ({b.report_summary.photos_count})
            </div>
            <Link to={`/cliente/reservas/${b.id}/relatorio`}
                  className="text-[10px] uppercase tracking-widest text-[#d4af37] hover:underline">
              Ver todas →
            </Link>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            {b.report_summary.photos_preview.map((p) => (
              <a key={p.id} href={`/api/files/${p.storage_path}`}
                 target="_blank" rel="noreferrer"
                 className="block aspect-square overflow-hidden rounded-sm border border-[#1a4640] hover:border-[#d4af37]">
                <img src={`/api/files/${p.storage_path}`} alt={p.room || ""}
                     className="w-full h-full object-cover" loading="lazy" />
              </a>
            ))}
          </div>
          {(b.report_summary.issues_count > 0 || b.report_summary.extras_count > 0) && (
            <div className="mt-3 flex flex-wrap gap-2 text-[10px] uppercase tracking-widest">
              {b.report_summary.issues_count > 0 && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-[#f59e0b]/40 text-[#f59e0b] rounded-sm">
                  <AlertTriangle size={10} /> {b.report_summary.issues_count} problema{b.report_summary.issues_count === 1 ? "" : "s"}
                </span>
              )}
              {b.report_summary.extras_count > 0 && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-[#d4af37]/40 text-[#d4af37] rounded-sm">
                  <Plus size={10} /> {b.report_summary.extras_count} extra{b.report_summary.extras_count === 1 ? "" : "s"}
                </span>
              )}
            </div>
          )}
        </section>
      )}

      {/* Invoice */}
      {b.invoice && (
        <section className="lux-card p-5" data-testid="detail-invoice-section">
          <div className="eyebrow flex items-center gap-2 text-[#d4af37] mb-3">
            <Receipt size={12} /> Fatura
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#a0b2af]">Código</div>
              <div className="font-mono text-lg text-[#fdfbf7] mt-0.5 flex items-center gap-2">
                <span data-testid="invoice-number">{b.invoice.number}</span>
                <button onClick={() => copy(b.invoice.number, "Código da fatura")}
                        className="text-[#a0b2af] hover:text-[#d4af37]"
                        aria-label="Copiar código">
                  <Copy size={13} />
                </button>
              </div>
              <div className="text-xs text-[#a0b2af] mt-1.5">
                Estado: <span className={b.invoice.status === "sent" || b.invoice.status === "approved" ? "text-[#10b981]" : "text-[#f59e0b]"}>
                  {b.invoice.status === "draft" && "Em rascunho"}
                  {b.invoice.status === "sent" && "Enviada"}
                  {b.invoice.status === "approved" && "Aprovada"}
                  {!["draft", "sent", "approved"].includes(b.invoice.status) && b.invoice.status}
                </span>
              </div>
            </div>
            {b.invoice.status !== "draft" && (
              <a href={`/api/invoices/${b.invoice.id}/pdf`}
                 target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-widest border border-[#d4af37] text-[#d4af37] hover:bg-[#d4af37]/10 rounded-sm"
                 data-testid="invoice-pdf-btn">
                <Download size={12} /> PDF
              </a>
            )}
          </div>
        </section>
      )}

      {/* Core info grid */}
      <section className="grid sm:grid-cols-2 gap-3">
        <InfoCard icon={<Calendar size={14} />} label="Data"
                  value={b.scheduled_date}
                  hint={b.scheduled_time ? `às ${b.scheduled_time}` : "Hora a combinar"} />
        <InfoCard icon={<Clock size={14} />} label="Duração"
                  value={`${b.duration || "—"} min`} />
        <InfoCard icon={<HomeIcon size={14} />} label="Serviço / Alojamento"
                  value={b.apartment_name || b.apt_type}
                  hint={b.apartment_name ? b.apt_type : (b.service_kind || "standard")} />
        <InfoCard icon={<Euro size={14} />} label="Valor"
                  value="A combinar"
                  hint="Confirmado por telefone / e-mail"
                  gold />
      </section>

      {/* Assigned professional */}
      <section className="lux-card p-5" data-testid="professional-section">
        <div className="eyebrow flex items-center gap-2 text-[#d4af37] mb-2">
          <UserCheck size={12} /> Profissional atribuído
        </div>
        <div className="text-sm text-[#fdfbf7]">
          {b.assigned_employee_name || (
            <span className="text-[#a0b2af] italic">
              Ainda não foi atribuído — a equipa será confirmada antes da limpeza.
            </span>
          )}
        </div>
      </section>

      {/* Guest / access */}
      <section className="lux-card p-5 space-y-3" data-testid="access-section">
        <div className="eyebrow flex items-center gap-2 text-[#d4af37]">
          <User size={12} /> Acesso no dia da limpeza
        </div>
        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          <Field label="Hóspede / contacto" value={b.guest_name} icon={<User size={13} />} />
          <Field label="Telefone" value={b.guest_phone} icon={<Phone size={13} />} mono
                 onCopy={b.guest_phone ? () => copy(b.guest_phone, "Telefone") : null} />
          <Field label="Morada" value={address || b.entry_address}
                 sub={addressLine2} icon={<MapPin size={13} />} wide />
          <Field label="Instruções de entrada" value={b.entry_instructions}
                 icon={<FileText size={13} />} wide />
          {b.notes && (
            <Field label="Observações" value={b.notes}
                   icon={<FileText size={13} />} wide />
          )}
        </div>
      </section>

      {/* Extras */}
      {b.extras?.length > 0 && (
        <section className="lux-card p-5" data-testid="extras-section">
          <div className="eyebrow text-[#d4af37]">Extras na reserva</div>
          <ul className="mt-2 space-y-1.5 text-sm text-[#fdfbf7]">
            {b.extras.map((e, i) => {
              const key = typeof e === "string" ? e : (e.id || e.name);
              const note = b.extras_notes?.[key];
              return (
                <li key={i} className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#d4af37] mt-2 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div>{typeof e === "string" ? e : (e.name || e.title)}</div>
                    {note && (
                      <div className="text-[11px] text-[#a0b2af] italic mt-0.5">"{note}"</div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {/* Cleaning scope */}
      <CleaningScope testId="detail-scope" />

      {/* Payment info */}
      <section className="lux-card p-5 space-y-3" data-testid="payment-section">
        <div className="eyebrow flex items-center gap-2 text-[#d4af37]">
          <CreditCard size={12} /> Pagamento
        </div>
        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          <Field label="Estado" value={paid ? "Pago" : "Pendente"}
                 valueClass={paid ? "text-[#10b981]" : "text-[#f59e0b]"} />
          <Field label="Método" value={b.paid_via ? b.paid_via.toUpperCase() : "—"} />
          <Field label="Referência bancária" value={b.payment_reference} mono
                 onCopy={b.payment_reference ? () => copy(b.payment_reference, "Referência") : null} />
          {paid && b.paid_at && (
            <Field label="Pago em" value={formatDateTime(b.paid_at)} />
          )}
        </div>
      </section>

      {/* Actions */}
      {(cancellable && (onCancel || onEdit)) && (
        <div className="flex flex-wrap gap-3 pt-4 border-t border-[#1a4640]">
          {onEdit && (
            <button onClick={onEdit}
                    title="Submeter pedido de alteração para aprovação da administração"
                    className="px-4 py-2 text-xs uppercase tracking-widest border border-[#d4af37]/60 text-[#d4af37] hover:bg-[#d4af37]/10 rounded-sm"
                    data-testid="detail-edit-btn">
              Pedir alteração
            </button>
          )}
          {onCancel && (
            <button onClick={onCancel}
                    className="px-4 py-2 text-xs uppercase tracking-widest border border-[#ef4444]/60 text-[#fca5a5] hover:bg-[#ef4444]/10 rounded-sm"
                    data-testid="detail-cancel-btn">
              Cancelar reserva
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function InfoCard({ icon, label, value, hint, gold }) {
  return (
    <div className="lux-card p-4">
      <div className="eyebrow flex items-center gap-2 text-[#a0b2af] text-[10px]">
        {icon} {label}
      </div>
      <div className={`font-display text-xl mt-1.5 ${gold ? "text-[#d4af37]" : "text-[#fdfbf7]"}`}>
        {value || "—"}
      </div>
      {hint && <div className="text-xs text-[#a0b2af] mt-1">{hint}</div>}
    </div>
  );
}

function Field({ label, value, sub, icon, mono, wide, valueClass, onCopy }) {
  return (
    <div className={wide ? "sm:col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-widest text-[#a0b2af] flex items-center gap-1.5">
        {icon} {label}
      </div>
      <div className={`mt-1 flex items-center gap-2 ${mono ? "font-mono" : ""} ${valueClass || "text-[#fdfbf7]"}`}>
        <span className="break-words">{value || "—"}</span>
        {onCopy && value && (
          <button onClick={onCopy} className="text-[#a0b2af] hover:text-[#d4af37]"
                  aria-label="Copiar">
            <Copy size={12} />
          </button>
        )}
      </div>
      {sub && <div className="text-xs text-[#a0b2af] mt-0.5">{sub}</div>}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: { label: "Por confirmar", cls: "border-[#f59e0b]/50 text-[#f59e0b] bg-[#f59e0b]/5" },
    confirmed: { label: "Confirmada", cls: "border-[#d4af37]/60 text-[#d4af37] bg-[#d4af37]/5" },
    in_progress: { label: "Em curso", cls: "border-[#3b82f6]/60 text-[#93c5fd] bg-[#3b82f6]/5" },
    completed: { label: "Concluída", cls: "border-[#10b981]/60 text-[#10b981] bg-[#10b981]/5" },
    cancelled: { label: "Cancelada", cls: "border-[#ef4444]/60 text-[#fca5a5] bg-[#ef4444]/5" },
  };
  const s = map[status] || { label: statusLabel(status), cls: "border-[#1a4640] text-[#a0b2af]" };
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-[10px] uppercase tracking-widest border rounded-sm ${s.cls}`}>
      <CheckCircle2 size={10} /> {s.label}
    </span>
  );
}

function formatDateTime(iso) {
  try {
    return new Date(iso).toLocaleString("pt-PT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
